import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { AgentSessionType } from "schemas/dashboard";
import { Map } from "schemas/functions";
import theme from "styles/theme";

import {
  TemplateContentType,
  TemplateMethod,
} from "features/Influencer/ContactList/schema";
import PickTemplateMethodForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/PickTemplateMethodForm";
import PickTemplateTypeForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/PickTemplateTypeForm";
import TemplatePromptForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/TemplatePromptForm";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import EditTemplateDialog from "../../EditTemplate";
import { FormAnswer, TemplateCreatePage } from "../schema";
import Chat from "./Chat";

const ConversationalTemplate = () => {
  const { setCreateTemplateOnboardingDialogOpen } = useContext(
    OnboardingDialogsContext,
  );
  const { editTemplateDialogId, setEditTemplateDialogId, outreachTemplates } =
    useContext(OutreachTemplatesContext);
  const { slideOutRight, slideInRight, slideOutLeft, slideInLeft, slideProps } =
    useContext(SlideAnimationContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    templates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);
  const isUserFirstTemplate = templates?.filter((x) => x.id!)?.length === 0;

  const hasExistingTemplates =
    outreachTemplates && outreachTemplates.length > 0;
  const { setErrorAlert } = useContext(AlertContext);
  const [agentSessionId, setAgentSessionId] = useState<number | null>(null);
  const [form, setForm] = useState<FormAnswer>({
    templateContentType: undefined,
    userPrompt: undefined,
  });
  const [page, setTemplateCreatePage] = useState<TemplateCreatePage>(
    TemplateCreatePage.method,
  );

  const handleClose = () => {
    setCreateTemplateDialogOpen(false);
    setCurrentTemplateContentType(TemplateContentType.OUTREACH);
  };

  const handleNext = async (method?: TemplateMethod) => {
    await slideOutLeft();
    trackEvent("Create Template Dialog Form - Next Button Clicked", {
      page,
      method,
    });

    if (page === TemplateCreatePage.method) {
      if (method === TemplateMethod.from_scratch) {
        setTemplateCreatePage(TemplateCreatePage.import);
      } else if (method === TemplateMethod.use_bento_ai) {
        setTemplateCreatePage(TemplateCreatePage.questions);
      }
    } else if (page === TemplateCreatePage.bentoAi) {
      setTemplateCreatePage(TemplateCreatePage.edit);
      setCreateTemplateDialogOpen(false);
    }

    slideInLeft();
  };

  const onSuccess = async () => {
    await fetchOutreachTemplates();
    handleClose();
  };

  const handleBack = async () => {
    trackEvent("Create Template Dialog Form - Back Button Clicked", {
      page,
    });

    await slideOutRight();
    if (page === TemplateCreatePage.method) {
      handleClose();
      return;
    }
    if (
      page === TemplateCreatePage.import ||
      page === TemplateCreatePage.questions
    ) {
      setTemplateCreatePage(TemplateCreatePage.method);
    } else if (page === TemplateCreatePage.prompt) {
      setTemplateCreatePage(TemplateCreatePage.questions);
    } else if (page === TemplateCreatePage.bentoAi) {
      // Go back to prompt if user has existing templates, otherwise questions
      if (hasExistingTemplates) {
        setTemplateCreatePage(TemplateCreatePage.prompt);
      } else {
        setTemplateCreatePage(TemplateCreatePage.questions);
      }
    }
    slideInRight();
  };

  const handleSubmitForm = async (formData: Map) => {
    await slideOutLeft();
    // If user has existing templates and selected Outreach, show prompt page first
    if (hasExistingTemplates) {
      setTemplateCreatePage(TemplateCreatePage.prompt);
      slideInLeft();
      return;
    }
    // Otherwise, go directly to bento AI

    await initAgentSession(formData);
  };

  const handlePromptSubmit = async () => {
    await slideOutLeft();
    trackEvent("Create Template Dialog Form - User Submitted Custom Prompt", {
      prompt: form.userPrompt,
    });
    await initAgentSession(form);
  };

  const initAgentSession = async (formData: Map) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/init`,
        "POST",
        {},
        { ...formData, type: AgentSessionType.template },
      );
      setAgentSessionId(res.session.id);
      slideInLeft();
      setTemplateCreatePage(TemplateCreatePage.bentoAi);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isUserFirstTemplate) {
    setCreateTemplateDialogOpen(false);
    setCreateTemplateOnboardingDialogOpen(true);
    return <></>;
  }

  return (
    <>
      <Slide
        in={slideProps.in}
        direction={slideProps.direction}
        easing={{
          enter: theme.transitions.easing.easeInOut,
          exit: theme.transitions.easing.easeIn,
        }}
        timeout={{
          enter: ENTER_DELAY,
          exit: EXIT_DELAY,
        }}
      >
        <Grid item xs={12}>
          {page === TemplateCreatePage.method && (
            <PickTemplateMethodForm handleNext={handleNext} />
          )}
          {page === TemplateCreatePage.import && (
            <EditTemplateDialog
              isNew
              onSuccess={onSuccess}
              propsHandleClose={handleBack}
            />
          )}
          {page === TemplateCreatePage.questions && (
            <PickTemplateTypeForm
              handleSubmitForm={handleSubmitForm}
              handleBack={handleBack}
              form={form}
              setForm={setForm}
            />
          )}
          {page === TemplateCreatePage.prompt && (
            <TemplatePromptForm
              handleSubmit={handlePromptSubmit}
              handleBack={handleBack}
              form={form}
              setForm={setForm}
            />
          )}
          {page === TemplateCreatePage.bentoAi && form?.templateContentType && (
            <Chat
              agentSessionId={agentSessionId}
              contentType={form.templateContentType}
              handleNext={handleNext}
              handleBack={handleBack}
            />
          )}
          {page === TemplateCreatePage.edit && editTemplateDialogId && (
            <EditTemplateDialog
              propsTemplateId={editTemplateDialogId}
              propsHandleClose={() => {
                setEditTemplateDialogId(null);
              }}
            />
          )}
        </Grid>
      </Slide>
    </>
  );
};

export default ConversationalTemplate;
